import { useState } from 'react'

import {
  type PlanCounterpartyMismatchPartner,
  PlanCounterpartyMismatchSide,
} from '@ebalance/proto/bffuser/v1/submission_management_model_pb'
import { useTranslation } from 'react-i18next'

import { SecondaryButton, VStack } from '@enechain/ecloud-designsystem'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { formatKomaRanges } from '~/models/komaRange'
import { useGetPlanCounterpartyMismatch } from '~/pages/ProcurementPlanDetail/hooks/useGetPlanCounterpartyMismatch'
import { useResolvePlanCounterpartyMismatch } from '~/pages/ProcurementPlanDetail/hooks/useResolvePlanCounterpartyMismatch'
import { hasUnresolvedMismatch } from '~/pages/ProcurementPlanDetail/models/hasUnresolvedMismatch'
import { partnerDisplayName } from '~/pages/ProcurementPlanDetail/models/mismatchItems'
import type { PlanTarget } from '~/pages/ProcurementPlanDetail/models/planTarget'

type Props = {
  /** 対象の計画 (版の ID か業務キー。`HandlingMemoPanel` から受け取る)。 */
  target: PlanTarget
}

/** コマ範囲を "HH:MM〜HH:MM" にする (終端コマの終了時刻まで)。 */
/**
 * 「取引相手間不整合解消」ボタンと確認ダイアログ (AC-PSM-063 / 064 / 065)。
 *
 * ボタンは read が成功し `featureEnabled && hasUnresolved` の時だけ出す
 * (無効表示にはしない。取得中・失敗も非表示)。workflow readonly・提出ステータスに
 * よらず表示する (解消は提出ステータスを変えない記録操作のため)。
 * 解消の取り消し UI は作らない (AC-PSM-066)。
 */
export const ResolveCounterpartyMismatch = ({
  target,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const [isOpen, setOpen] = useState(false)
  const { data } = useGetPlanCounterpartyMismatch(target)
  const { resolve, isResolving } = useResolvePlanCounterpartyMismatch(
    target,
    () => {
      setOpen(false)
    },
  )

  const hasUnresolved = hasUnresolvedMismatch(data)
  // STALE 後の再取得等で未解消が無くなったら開閉 state も戻す。残すと、後の再取得で
  // 未解消に戻った時に利用者が押していないのにダイアログが開く (AC-PSM-064)
  if (!hasUnresolved && isOpen) {
    setOpen(false)
  }

  if (data === undefined || !hasUnresolved) {
    return null
  }

  const sideLabel = (p: PlanCounterpartyMismatchPartner): string => {
    switch (p.side) {
      case PlanCounterpartyMismatchSide.PROCUREMENT:
        return t(
          'page:procurementPlanDetail.counterpartyMismatch.sideProcurement',
        )
      case PlanCounterpartyMismatchSide.SALES:
        return t('page:procurementPlanDetail.counterpartyMismatch.sideSales')
      default:
        return ''
    }
  }
  const partnerLabel = (p: PlanCounterpartyMismatchPartner): string => {
    const name = partnerDisplayName(p)
    return name !== undefined
      ? `${name} (${p.counterpartyCode})`
      : p.counterpartyCode
  }

  const body = (
    <VStack align="stretch" gap={3}>
      <p>{t('page:procurementPlanDetail.counterpartyMismatch.dialogBody')}</p>
      <VStack align="stretch" gap={1}>
        {data.partners.map((p) => (
          <div key={`${p.counterpartyCode}-${p.side}`}>
            {[partnerLabel(p), sideLabel(p), formatKomaRanges(p.ranges)]
              .filter((s) => s !== '')
              .join(' / ')}
          </div>
        ))}
      </VStack>
    </VStack>
  )

  return (
    <>
      <SecondaryButton
        onClick={() => {
          setOpen(true)
        }}
        size="sm"
      >
        {t('page:procurementPlanDetail.counterpartyMismatch.buttonLabel')}
      </SecondaryButton>
      <ConfirmDialog
        body={body}
        confirmLabel={t(
          'page:procurementPlanDetail.counterpartyMismatch.confirmLabel',
        )}
        isLoading={isResolving}
        isOpen={isOpen && data.hasUnresolved}
        onCancel={() => {
          setOpen(false)
        }}
        onConfirm={() => {
          resolve(data.resolutionToken)
        }}
        title={t('page:procurementPlanDetail.counterpartyMismatch.dialogTitle')}
      />
    </>
  )
}
